import moment from 'moment';

import { Employees } from '~/data/employees';
import { LegalEntity } from '~/data/legalEntity';
import { AbsencesReportData } from '~/data/reports';
import { AbsenceTypeEnum } from '~/services/Http/client.people.generated';
import { convertToDateOrUndefined } from '~/utils/helpers/date';

export type AbsencesReportRow_Employee = {
  employeeId: number;
  firstName: string;
  lastName: string;
};

export type AbsencesReportRow_Position = {
  organizationUnit: string;
  organizationUnitNumber: string;
  manager: string;
  managerNumber: string;
  legalEntity: string;
  costCenter: string;
  costCenterCode: string;
};

export type AbsencesReportRow_Absences = {
  absenceDaysTotal: number;
  absencesDaysInPeriod: number;
  startDate: Date;
  endDate?: Date;
  type: string;
  reason: string;
  info: string;
  status: string;
};

export type AbsencesReportRow_PersonnelGroups = {
  personnelGroup1: string;
  personnelGroup2: string;
  personnelGroup3: string;
  personnelGroup4: string;
  personnelGroup5: string;
};

export type AbsencesReportRow = {
  rowId: string;
} & AbsencesReportRow_Employee &
  AbsencesReportRow_Position &
  AbsencesReportRow_Absences &
  AbsencesReportRow_PersonnelGroups;

export const convertAbsencesReportDataToRows = (props: {
  data: AbsencesReportData[];
  getEmployeeById: (employeeId?: number) => Employees | undefined;
  getLegalEntityById: (legalEntityId: number) => LegalEntity | undefined;
}): AbsencesReportRow[] => {
  const { data, getEmployeeById, getLegalEntityById } = props;

  return data
    .map((entry): AbsencesReportRow[] => {
      const {
        fEmpId,
        fEmpFirstName,
        fEmpLastName,
        fEmpUnit,
        fEmpUnitNumber,
        fEmpManagerId,
        fEmpLegalEntityId,
        fEmpCostCenterName,
        fEmpCostCenterCode,
        fEmpPersonnelGroups: {
          fEmpPersonnelGroup1,
          fEmpPersonnelGroup2,
          fEmpPersonnelGroup3,
          fEmpPersonnelGroup4,
          fEmpPersonnelGroup5,
        },
        fEmpAbsenceShortHistory,
        fEmpAbsenceLongHistory,
      } = entry;

      const hasShortAbsences = fEmpAbsenceShortHistory.length > 0;
      const hasLongAbsences = fEmpAbsenceLongHistory.length > 0;

      if (!hasShortAbsences && !hasLongAbsences) {
        return [];
      }

      const managerDetails = getEmployeeById(fEmpManagerId);
      const managerFullName = managerDetails
        ? `${managerDetails.fEmpFirstName} ${managerDetails.fEmpLastName}`
        : '';

      const legalEntity = getLegalEntityById(fEmpLegalEntityId);

      const baseRowData = {
        firstName: fEmpFirstName,
        lastName: fEmpLastName,
        employeeId: fEmpId,
        organizationUnit: fEmpUnit,
        organizationUnitNumber: fEmpUnitNumber,
        manager: managerFullName,
        managerNumber: managerDetails?.fEmpPersonNumber ?? '',
        legalEntity: legalEntity?.fLegalEntityName ?? '',
        costCenter: fEmpCostCenterName,
        costCenterCode: fEmpCostCenterCode,
        personnelGroup1: fEmpPersonnelGroup1 ?? '',
        personnelGroup2: fEmpPersonnelGroup2 ?? '',
        personnelGroup3: fEmpPersonnelGroup3 ?? '',
        personnelGroup4: fEmpPersonnelGroup4 ?? '',
        personnelGroup5: fEmpPersonnelGroup5 ?? '',
      };

      const shortAbsenceRows = hasShortAbsences
        ? fEmpAbsenceShortHistory.map(
            (absence): AbsencesReportRow => ({
              ...baseRowData,
              rowId: absence.fEmpAbsenceUid,
              absenceDaysTotal: absence.fEmpAbsenceDaysTotal,
              absencesDaysInPeriod: absence.fEmpAbsenceDaysInPeriod,
              startDate: moment(absence.fEmpAbsenceStartDate).toDate(),
              endDate: moment(absence.fEmpAbsenceEndDate).toDate(),
              type: AbsenceTypeEnum.SHORT,
              reason: absence.fEmpAbsenceReason,
              info: absence.fEmpAbsenceInfo,
              status: absence.fEmpAbsenceStatus,
            }),
          )
        : [];

      const longAbsenceRows = hasLongAbsences
        ? fEmpAbsenceLongHistory.map(
            (absence): AbsencesReportRow => ({
              ...baseRowData,
              rowId: absence.fEmpAbsenceUid,
              absenceDaysTotal: absence.fEmpAbsenceDaysTotal,
              absencesDaysInPeriod: absence.fEmpAbsenceDaysInPeriod,
              startDate: moment(absence.fEmpAbsenceStartDate).toDate(),
              endDate: convertToDateOrUndefined(absence.fEmpAbsenceEndDate),
              type: AbsenceTypeEnum.LONG,
              reason: absence.fEmpAbsenceReason,
              info: absence.fEmpAbsenceInfo,
              status: absence.fEmpAbsenceStatus,
            }),
          )
        : [];

      return [...shortAbsenceRows, ...longAbsenceRows];
    })
    .flat();
};
